import React, { useState, useEffect, useRef } from 'react';
import { Search, Users, UserPlus, Shield, ChevronRight, ChevronLeft, User, MapPin, Target, Check, X, Clock, Send, Bell, Camera, MessageSquare } from 'lucide-react';
import { collection, query, where, getDocs, addDoc, updateDoc, doc, arrayUnion, serverTimestamp, orderBy, limit, setDoc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { FIRESTORE_PATHS } from '../config/appConfig';
import { useUser } from '../contexts/UserContext';
import { useToast } from '../contexts/ToastContext'; // Import Toast
import logger from '../utils/logger';
import DiscoverView from './Dugout/DiscoverView';
import RequestsView from './Dugout/RequestsView';
import FriendsView from './Dugout/FriendsView';
import TeamsView from './Dugout/TeamsView';
import ChatView from './Dugout/ChatView';
import InboxView from './Dugout/InboxView';
import CompanionPairingModal from './Dugout/CompanionPairingModal';
import CreateTeamModal from './Dugout/CreateTeamModal';
import UserProfileModal from './Dugout/UserProfileModal'; // Import Profile Modal
import TeamDetailModal from './Dugout/TeamDetailModal'; // Import Team Detail Modal

// ============================================
// MAIN COMPONENT
// ============================================

const DugoutModule = () => {
    const { user, userProfile } = useUser();
    const { success, error, info } = useToast(); // Use Toast
    const [activeTab, setActiveTab] = useState('discover'); // 'discover', 'requests', 'friends', 'Teams'

    // Data State
    const [friendRequests, setFriendRequests] = useState([]);
    const [teamRequests, setTeamRequests] = useState([]);
    const [friends, setFriends] = useState([]);
    const [teams, setTeams] = useState([]);
    const [friendStatuses, setFriendStatuses] = useState({}); // New state for online status
    const [pendingRequests, setPendingRequests] = useState(new Set()); // Track optimistic updates

    // UI State for Chat & Pairing & Team Creation & Details
    const [activeChatFriend, setActiveChatFriend] = useState(null);
    const [showPairingModal, setShowPairingModal] = useState(false);
    const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null); // For UserProfileModal
    const [selectedTeam, setSelectedTeam] = useState(null); // For TeamDetailModal

    const isOnline = (lastActive) => {
        if (!lastActive) return false;
        try {
            const now = new Date();
            const activeTime = lastActive.toDate ? lastActive.toDate() : new Date(lastActive);
            const diffInSeconds = (now - activeTime) / 1000;
            // Be lenient with clock drift - 5 minutes
            return diffInSeconds < 300; 
        } catch (err) {
            return false;
        }
    };

    // --- Load Data using real-time onSnapshot listeners (was 5s polling) ---
    useEffect(() => {
        if (!user?.uid) return;

        const unsubscribers = [];

        // 1. Real-time listener: Friend Requests (incoming pending)
        const friendReqQuery = query(
            collection(db, FIRESTORE_PATHS.FRIEND_REQUESTS),
            where('toUid', '==', user.uid),
            where('status', '==', 'pending')
        );
        unsubscribers.push(onSnapshot(friendReqQuery, (snap) => {
            setFriendRequests(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        }, (err) => logger.error('Dugout', 'Friend requests listener error:', err)));

        // 2. Real-time listener: Friends list (Users/{userId}/Friends)
        const friendsRef = collection(db, FIRESTORE_PATHS.USERS, user.uid, FIRESTORE_PATHS.FRIENDS);
        unsubscribers.push(onSnapshot(query(friendsRef), async (snap) => {
            const loadedFriends = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            setFriends(loadedFriends);

            // Fetch statuses for friends from Users/{friendId}
            const statusUpdates = {};
            for (const f of loadedFriends) {
                try {
                    const sSnap = await getDoc(doc(db, FIRESTORE_PATHS.USERS, f.uid));
                    if (sSnap.exists()) {
                        statusUpdates[f.uid] = sSnap.data().lastActive;
                    }
                } catch (err) {
                    // Silently handle individual status fetch failures
                }
            }
            setFriendStatuses(statusUpdates);
        }, (err) => logger.error('Dugout', 'Friends listener error:', err)));

        // 3. Real-time listener: User's Teams subcollection
        const teamsRef = collection(db, FIRESTORE_PATHS.USERS, user.uid, FIRESTORE_PATHS.TEAMS);
        unsubscribers.push(onSnapshot(query(teamsRef), async (snap) => {
            const teamPromises = snap.docs.map(async (teamDoc) => {
                const teamData = teamDoc.data();
                const teamId = teamData.teamId;
                if (teamId) {
                    try {
                        const teamSnap = await getDoc(doc(db, FIRESTORE_PATHS.TEAMS, teamId));
                        if (teamSnap.exists()) {
                            return { id: teamId, ...teamSnap.data() };
                        }
                    } catch (err) {
                        logger.error('Dugout', `Error fetching team ${teamId}:`, err);
                    }
                }
                return null;
            });
            const loadedTeams = (await Promise.all(teamPromises)).filter(Boolean);
            setTeams(loadedTeams);
        }, (err) => logger.error('Dugout', 'Teams listener error:', err)));

        // 4. Real-time listener: My own team requests
        const myTeamReqQuery = query(
            collection(db, FIRESTORE_PATHS.TEAM_REQUESTS),
            where('userId', '==', user.uid),
            where('status', '==', 'pending')
        );
        unsubscribers.push(onSnapshot(myTeamReqQuery, async (snap) => {
            let allRequests = snap.docs.map(d => ({ id: d.id, ...d.data() }));

            // Also fetch incoming join requests to teams I own (one-time fetch)
            try {
                const myTeamsQuery = query(
                    collection(db, FIRESTORE_PATHS.TEAMS),
                    where('createdBy', '==', user.uid)
                );
                const myTeamsSnap = await getDocs(myTeamsQuery);
                const myTeamIds = myTeamsSnap.docs.map(d => d.id);

                if (myTeamIds.length > 0) {
                    const teamIdsToCheck = myTeamIds.slice(0, 10);
                    const incomingRequestsQuery = query(
                        collection(db, FIRESTORE_PATHS.TEAM_REQUESTS),
                        where('teamId', 'in', teamIdsToCheck),
                        where('type', '==', 'join_request'),
                        where('status', '==', 'pending')
                    );
                    const incomingSnap = await getDocs(incomingRequestsQuery);
                    const incomingRequests = incomingSnap.docs.map(d => ({ id: d.id, ...d.data() }));

                    const existingIds = new Set(allRequests.map(r => r.id));
                    incomingRequests.forEach(req => {
                        if (!existingIds.has(req.id)) {
                            allRequests.push(req);
                        }
                    });
                }
            } catch (err) {
                logger.error('Dugout', 'Error fetching incoming team requests:', err);
            }

            setTeamRequests(allRequests);
        }, (err) => logger.error('Dugout', 'Team requests listener error:', err)));

        return () => {
            unsubscribers.forEach(unsub => unsub());
        };
    }, [user, userProfile]);

    // --- Actions ---
    const handleSendFriendRequest = async (targetUser) => {
        if (!user?.uid) return;
        try {
            await addDoc(collection(db, FIRESTORE_PATHS.FRIEND_REQUESTS), {
                fromUid: user.uid,
                fromName: user.displayName || user.profile?.displayName || 'Unknown',
                toUid: targetUser.id,
                toName: targetUser.displayName,
                status: 'pending',
                createdAt: serverTimestamp()
            });
            success('Friend request sent!');
        } catch (err) {
            logger.error('Dugout', 'Error sending friend request:', err);
        }
    };

    const handleSendTeamRequest = async (targetTeam) => {
        if (!user?.uid) return;
        try {
            await addDoc(collection(db, FIRESTORE_PATHS.TEAM_REQUESTS), {
                userId: user.uid,
                userName: user.displayName || user.profile?.displayName || 'Unknown',
                teamId: targetTeam.id,
                teamName: targetTeam.meta?.name,
                type: 'join_request',
                status: 'pending',
                createdAt: serverTimestamp()
            });
            success('Join request sent!');
        } catch (err) {
            logger.error('Dugout', 'Error sending team request:', err);
        }
    };

    const handleAcceptFriend = async (request) => {
        try {
            // Update request status
            await updateDoc(doc(db, FIRESTORE_PATHS.FRIEND_REQUESTS, request.id), { status: 'accepted' });

            // Add to both users' Friends subcollections - organized structure
            const myName = user.displayName || userProfile?.displayName || 'Unknown';
            const newFriendForMe = { uid: request.fromUid, displayName: request.fromName };
            const newFriendForThem = { uid: user.uid, displayName: myName };

            await setDoc(doc(db, FIRESTORE_PATHS.USERS, user.uid, FIRESTORE_PATHS.FRIENDS, request.fromUid), {
                uid: request.fromUid,
                displayName: request.fromName,
                addedAt: serverTimestamp()
            });
            await setDoc(doc(db, FIRESTORE_PATHS.USERS, request.fromUid, FIRESTORE_PATHS.FRIENDS, user.uid), {
                uid: user.uid,
                displayName: myName,
                addedAt: serverTimestamp()
            });

            // Update local state immediately
            setFriends(prev => [...prev, newFriendForMe]);

            // Remove from requests list
            setFriendRequests(prev => prev.filter(r => r.id !== request.id));

            success(`You are now companions with ${request.fromName}!`);

        } catch (err) {
            logger.error('Dugout', 'Error accepting friend:', err);
            error("Failed to accept friend request.");
        }
    };

    const handleRejectFriend = async (request) => {
        try {
            await updateDoc(doc(db, FIRESTORE_PATHS.FRIEND_REQUESTS, request.id), { status: 'rejected' });
        } catch (err) {
            logger.error('Dugout', 'Error rejecting friend:', err);
        }
    };

    const handleAcceptTeam = async (request) => {
        try {
            // Update request status
            await updateDoc(doc(db, FIRESTORE_PATHS.TEAM_REQUESTS, request.id), { status: 'accepted' });

            // Add user to team roster
            await updateDoc(doc(db, FIRESTORE_PATHS.TEAMS, request.teamId), {
                [`roster.${user.uid}`]: { role: 'member', status: 'active' }
            });

            // Add team to user's Teams subcollection - organized structure
            await setDoc(doc(db, FIRESTORE_PATHS.USERS, user.uid, FIRESTORE_PATHS.TEAMS, request.teamId), {
                teamId: request.teamId,
                teamName: request.teamName,
                joinedAt: serverTimestamp()
            });

            // Update local state immediately by fetching the full team data
            const teamSnap = await getDoc(doc(db, FIRESTORE_PATHS.TEAMS, request.teamId));
            if (teamSnap.exists()) {
                setTeams(prev => [...prev, { id: request.teamId, ...teamSnap.data() }]);
            }

            // Remove from requests list
            setTeamRequests(prev => prev.filter(r => r.id !== request.id));

            success(`Joined ${request.teamName}!`);
        } catch (err) {
            logger.error('Dugout', 'Error accepting team invite:', err);
            error("Failed to join team.");
        }
    };

    const handleRejectTeam = async (request) => {
        try {
            await updateDoc(doc(db, FIRESTORE_PATHS.TEAM_REQUESTS, request.id), { status: 'rejected' });
        } catch (err) {
            logger.error('Dugout', 'Error rejecting team invite:', err);
        }
    };

    // --- Social 2.0 Actions ---

    const handleUnfriend = async (friend) => {
        if (!window.confirm(`Are you sure you want to remove ${friend.displayName}?`)) return;
        try {
            // Delete from both users' Friends subcollections
            const { deleteDoc } = await import('firebase/firestore');
            
            // Delete from my friends
            await deleteDoc(doc(db, FIRESTORE_PATHS.USERS, user.uid, FIRESTORE_PATHS.FRIENDS, friend.uid));
            // Delete from their friends
            await deleteDoc(doc(db, FIRESTORE_PATHS.USERS, friend.uid, FIRESTORE_PATHS.FRIENDS, user.uid));

            setFriends(prev => prev.filter(f => f.uid !== friend.uid));
            setSelectedUser(null);
            info(`Removed ${friend.displayName} from companions.`);
        } catch (err) {
            logger.error('Dugout', 'Error unfriending:', err);
            error("Failed to unfriend.");
        }
    };

    const handleBlock = (friend) => {
        if (!window.confirm(`Block ${friend.displayName}? They won't be able to contact you.`)) return;
        handleUnfriend(friend); // For now just unfriend
        info("User blocked.");
    };

    const handleLeaveTeam = async (teamId) => {
        if (!window.confirm("Are you sure you want to leave this team?")) return;
        try {
            const { deleteField, deleteDoc } = await import('firebase/firestore');

            // 1. Remove from user's Teams subcollection - organized structure
            await deleteDoc(doc(db, FIRESTORE_PATHS.USERS, user.uid, FIRESTORE_PATHS.TEAMS, teamId));

            // 2. Remove me from team roster
            await updateDoc(doc(db, FIRESTORE_PATHS.TEAMS, teamId), {
                [`roster.${user.uid}`]: deleteField()
            });

            setTeams(prev => prev.filter(t => t.id !== teamId));
            setSelectedTeam(null);
            info("You have left the team.");
        } catch (err) {
            logger.error('Dugout', 'Error leaving team:', err);
            error("Failed to leave team.");
        }
    };

    const handleKickMember = async (memberUid) => {
        if (!selectedTeam) return;
        if (!window.confirm("Kick this member?")) return;
        try {
            const { deleteField, deleteDoc } = await import('firebase/firestore');
            
            // 1. Remove from team roster
            await updateDoc(doc(db, FIRESTORE_PATHS.TEAMS, selectedTeam.id), {
                [`roster.${memberUid}`]: deleteField()
            });

            // 2. Remove team from user's Teams subcollection
            await deleteDoc(doc(db, FIRESTORE_PATHS.USERS, memberUid, FIRESTORE_PATHS.TEAMS, selectedTeam.id));

            // Update local selectedTeam state to reflect change
            const newRoster = { ...selectedTeam.roster };
            delete newRoster[memberUid];
            const updatedTeam = { ...selectedTeam, roster: newRoster };

            setSelectedTeam(updatedTeam);
            setTeams(prev => prev.map(t => t.id === selectedTeam.id ? updatedTeam : t));

            info("Member kicked.");
        } catch (err) {
            logger.error('Dugout', 'Error kicking member:', err);
            error("Failed to kick member.");
        }
    };

    const handleDeleteTeam = async (teamId) => {
        if (!window.confirm("EXTREME WARNING: This will delete the team permanently. This cannot be undone.")) return;
        try {
            const { deleteDoc } = await import('firebase/firestore');
            await deleteDoc(doc(db, FIRESTORE_PATHS.TEAMS, teamId));
            setTeams(prev => prev.filter(t => t.id !== teamId));
            setSelectedTeam(null);
            success("Team disbanded.");
        } catch (err) {
            logger.error('Dugout', 'Error deleting team:', err);
            error("Failed to delete team.");
        }
    };

    const handleEditTeam = (team) => {
        // Placeholder for edit
        info("Edit feature coming soon!");
    };

    const tabs = [
        { id: 'discover', icon: Search, label: 'Discover' },
        { id: 'requests', icon: Bell, label: 'Requests', badge: friendRequests.length + teamRequests.length },
        { id: 'inbox', icon: MessageSquare, label: 'Inbox' },
        { id: 'friends', icon: Users, label: 'Companions' },
        { id: 'Teams', icon: Shield, label: 'Teams' },
    ];

    // If in a chat, show the chat view
    if (activeChatFriend) {
        return (
            <ChatView
                friend={activeChatFriend}
                onBack={() => setActiveChatFriend(null)}
                userId={user?.uid}
                friendOnline={isOnline(friendStatuses[activeChatFriend.uid])}
            />
        );
    }

    return (
        <div className="flex flex-col h-full bg-gray-900 text-white">
            {/* Header */}
            <div className="p-4 bg-gray-800 border-b border-gray-700">
                <h1 className="text-xl font-bold tracking-tight">The Dugout</h1>
                <p className="text-xs text-gray-500">Connect with players and teams</p>
            </div>

            {/* Tab Content */}
            {activeTab === 'discover' && (
                <DiscoverView
                    onSendFriendRequest={handleSendFriendRequest}
                    onSendTeamRequest={handleSendTeamRequest}
                    pendingRequests={pendingRequests} // Pass down optimistic state
                />
            )}
            {activeTab === 'requests' && (
                <RequestsView
                    friendRequests={friendRequests}
                    teamRequests={teamRequests}
                    onAcceptFriend={handleAcceptFriend}
                    onRejectFriend={handleRejectFriend}
                    onAcceptTeam={handleAcceptTeam}
                    onRejectTeam={handleRejectTeam}
                />
            )}
            {activeTab === 'inbox' && (
                <InboxView
                    userId={user.uid}
                    friends={friends}
                    onOpenChat={(friend) => setActiveChatFriend(friend)}
                    friendStatuses={friendStatuses}
                />
            )}
            {activeTab === 'friends' && (
                <FriendsView
                    friends={friends}
                    onOpenChat={(friend) => setActiveChatFriend(friend)} // Direct Chat
                    onOpenPairing={() => setShowPairingModal(true)}
                    onSelectFriend={(friend) => setSelectedUser(friend)} // Open Profile Modal
                    friendStatuses={friendStatuses}
                />
            )}
            {activeTab === 'Teams' && (
                <TeamsView
                    teams={teams}
                    onCreateTeam={() => setShowCreateTeamModal(true)}
                    onSelectTeam={(team) => setSelectedTeam(team)} // Open Team Detail Modal
                />
            )}

            {/* Bottom Tab Bar */}
            <div className="bg-gray-800 border-t border-gray-700 flex">
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex-1 py-3 flex flex-col items-center gap-1 relative transition-colors ${activeTab === tab.id ? 'text-neon-cyan' : 'text-gray-500 hover:text-gray-300'}`}
                    >
                        <tab.icon size={20} />
                        <span className="text-[10px] font-bold uppercase">{tab.label}</span>
                        {tab.badge > 0 && (
                            <span className="absolute top-1 right-1/4 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                                {tab.badge}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {/* Companion Pairing Modal */}
            {showPairingModal && (
                <CompanionPairingModal
                    onClose={() => setShowPairingModal(false)}
                    userId={user?.uid}
                />
            )}

            {/* Create Team Modal */}
            {showCreateTeamModal && (
                <CreateTeamModal
                    onClose={() => setShowCreateTeamModal(false)}
                    onTeamCreated={(newTeamId) => {
                        // Refresh teams list after creation
                        const loadNewTeam = async () => {
                            try {
                                const teamSnap = await getDoc(doc(db, FIRESTORE_PATHS.TEAMS, newTeamId));
                                if (teamSnap.exists()) {
                                    setTeams(prev => [...prev, { id: newTeamId, ...teamSnap.data() }]);
                                    success("Team created successfully!");
                                }
                            } catch (err) {
                                logger.error('Dugout', 'Error loading new team:', err);
                            }
                        };
                        return loadNewTeam();
                    }}
                    userId={user?.uid}
                    userName={user?.displayName || user?.profile?.displayName || 'Unknown'}
                />
            )}

            {/* User Profile Modal */}
            {selectedUser && (
                <UserProfileModal
                    user={selectedUser}
                    onClose={() => setSelectedUser(null)}
                    onChat={(user) => {
                        setSelectedUser(null);
                        setActiveChatFriend(user);
                    }}
                    onUnfriend={handleUnfriend}
                    onBlock={handleBlock}
                />
            )}

            {/* Team Detail Modal */}
            {selectedTeam && (
                <TeamDetailModal
                    team={selectedTeam}
                    onClose={() => setSelectedTeam(null)}
                    onLeaveTeam={handleLeaveTeam}
                    onKickMember={handleKickMember}
                    onDeleteTeam={handleDeleteTeam}
                    onEditTeam={handleEditTeam}
                />
            )}
        </div>
    );
};

export default DugoutModule;
